/**
 * Shortest query the admin account search will run. The scan is a sequential
 * `ILIKE '%q%'` over families and users — no index can serve it — so a
 * one-character needle costs a full pass and matches most of the fleet.
 *
 * Shared so the operator UI can say "type at least N characters" using the
 * same number the server enforces, rather than reporting a short query as
 * "no households found".
 */
export const MIN_ACCOUNT_SEARCH_LENGTH = 2;

/**
 * Households one search returns at most. A query this wide is an operator
 * narrowing in on one household, not paging a list, so the cap is a runaway
 * bound rather than a page size — there is no cursor.
 *
 * Shared so the UI can say a full result set was truncated. Without the
 * number, a capped result is indistinguishable from a complete one and the
 * operator narrows a query that was already hiding the match.
 */
export const ACCOUNT_SEARCH_LIMIT = 50;

/** Digits plus the punctuation a pasted phone number carries, and nothing else. */
const PHONE_QUERY_PATTERN = /^[\d\s()+.-]+$/;

/**
 * The digits of a query that reads as a phone number, or null for any other
 * query. A number arrives in whatever form the operator copied it —
 * `(312) 555-0142`, `+1 312.555.0142` — so it is matched on its digits alone;
 * a query holding a letter or an `@` is a name or an email instead.
 *
 * Null below {@link MIN_ACCOUNT_SEARCH_LENGTH} digits, so punctuation cannot
 * carry a one-digit query past the floor.
 *
 * Shared so the command palette lists by phone exactly the queries the account
 * search matches by phone.
 */
export function phoneQueryDigits(query: string): string | null {
  const trimmed = query.trim();
  if (!PHONE_QUERY_PATTERN.test(trimmed)) {
    return null;
  }
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= MIN_ACCOUNT_SEARCH_LENGTH ? digits : null;
}
